import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { RootState } from '../ApplicationState';
import { selectClassroom } from '../Classrooms';
import TailwindButton from '../components/TailwindButton';
import TypeformEmbed from '../Survey/SurveyEmbed';
import { hasTakweenSurveyCompleted, markTakweenSurveyComplete } from './takweenSurveyStorage';
import { TakweenSurveyRouteState } from './types';

const TAKWEEN_SURVEY_FORM_ID = 'Wo3Hp6df';

interface SurveyParams {
  classroomId: string;
  studentId: string;
}

const TakweenStudentSurveyPage: React.FC = () => {
  const classroom = useSelector((state: RootState) => selectClassroom(state));
  const userProfile = useSelector((state: RootState) => state.user.userProfile);
  const history = useHistory();
  const location = useLocation<TakweenSurveyRouteState>();
  const { classroomId, studentId } = useParams<SurveyParams>();
  const browserLanguage = typeof window !== 'undefined' ? window.navigator.language.toLowerCase() : 'en';
  const [selectedLanguage, setSelectedLanguage] = useState<'en' | 'ar'>(browserLanguage.startsWith('ar') ? 'ar' : 'en');

  const student = classroom?.listOfStudentProxies?.find((item) => item.userId === studentId);
  const isCompleted = hasTakweenSurveyCompleted(classroomId, studentId);

  const teacherName = `${userProfile?.firstName || ''} ${userProfile?.lastName || ''}`.trim();
  const studentName = `${student?.firstName || ''} ${student?.lastName || ''}`.trim();

  const hiddenFields = useMemo(
    () => ({
      teacher_id: userProfile?.userId || '',
      teacher_name: teacherName,
      student_id: studentId,
      student_name: studentName,
      classroom_id: classroomId,
      class_name: classroom?.className || '',
      district: classroom?.district || userProfile?.district || '',
      school_id: userProfile?.schoolId || classroom?.schoolId || '',
    }),
    [
      classroom?.className,
      classroom?.district,
      classroom?.schoolId,
      classroomId,
      studentId,
      studentName,
      teacherName,
      userProfile,
    ],
  );

  const handleSurveySubmit = () => {
    markTakweenSurveyComplete(classroomId, studentId);
    history.push('/takween/survey/confirmation', {
      ...location.state,
      classroomId,
    });
  };

  if (!classroom || !student) {
    return (
      <div className='min-h-screen bg-gray-50 p-4 md:p-6 lg:p-8'>
        <div className='mx-auto mt-8 max-w-lg rounded-lg bg-white p-8 text-center shadow-sm'>
          <h2 className='m-0 text-[2.6rem] text-gray-800'>Student not found</h2>
          <p className='mb-6 mt-4 text-[1.6rem] text-gray-500'>Return to the student list and try again.</p>
          <TailwindButton variant='secondary' onClick={() => history.push('/takween/survey/students', location.state)}>
            Back to Student List
          </TailwindButton>
        </div>
      </div>
    );
  }

  if (isCompleted) {
    return (
      <div className='min-h-screen bg-gray-50 p-4 md:p-6 lg:p-8'>
        <div className='mx-auto mt-8 max-w-lg rounded-lg bg-white p-8 text-center shadow-sm'>
          <h2 className='m-0 text-[2.6rem] text-gray-800'>Survey already completed</h2>
          <p className='mb-6 mt-4 text-[1.6rem] text-gray-500'>{studentName} has already completed this survey.</p>
          <TailwindButton variant='secondary' onClick={() => history.push('/takween/survey/students', location.state)}>
            Back to Student List
          </TailwindButton>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gray-50 p-4 md:p-6 lg:p-8'>
      <div className='mb-4 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between'>
        <div>
          <h1 className='m-0 text-[3rem] font-bold text-gray-800'>Student Post-Survey</h1>
          <p className='mt-1.5 text-[2rem] text-gray-500'>{studentName}</p>
        </div>
        <div className='flex flex-wrap gap-4'>
          <TailwindButton variant='secondary' onClick={() => history.push('/takween/survey/students', location.state)}>
            Back to Student List
          </TailwindButton>
          <TailwindButton
            variant='secondary'
            className='min-w-[220px]'
            onClick={() => setSelectedLanguage((current) => (current === 'en' ? 'ar' : 'en'))}
          >
            Switch to {selectedLanguage === 'en' ? 'Arabic' : 'English'}
          </TailwindButton>
        </div>
      </div>

      <div className='mb-4 inline-flex items-center rounded-full bg-indigo-50 px-4 py-1.5 text-[1.4rem] font-semibold text-gray-800'>
        Language: {selectedLanguage === 'en' ? 'English' : 'Arabic'}
      </div>

      <div className='rounded-lg bg-white p-4 shadow-sm'>
        <TypeformEmbed
          key={selectedLanguage}
          formId={TAKWEEN_SURVEY_FORM_ID}
          minHeight='680px'
          hiddenFields={hiddenFields}
          tracking={{
            'typeform-lang': selectedLanguage,
          }}
          onSubmit={handleSurveySubmit}
        />
      </div>
    </div>
  );
};

export default TakweenStudentSurveyPage;
